import { type ReactNode } from 'react'
import { ADS_PATH, readAdsLocation } from '../pages/ads/routing'
import {
  LayoutDashboard, ListChecks,
  Megaphone, Bot, MessageCircle, Box, Clock, Package, BarChart3,
  Folder, Store, UsersRound, Lightbulb, BookOpen,
  SlidersHorizontal, ScrollText, PieChart, ClipboardCheck, CirclePlus,
} from 'lucide-react'
// SellerMate W3.3 (Alerts). Its own line so it never collides with edits to
// the shared icon list above.
import { Siren } from 'lucide-react'
// Settings (PR 7): its own line for the same reason.
import { Settings as SettingsIcon } from 'lucide-react'
// Deals (PR 15): its own line for the same reason.
import { BadgePercent } from 'lucide-react'
// Labs (PR 6): its own line for the same reason.
import { Radio, Wrench } from 'lucide-react'

export type SidebarItem    = { type: 'item';    id: string; label?: string }
export type SidebarSection = { type: 'section'; id: string; label: string; children: SidebarNode[] }
export type SidebarNode    = SidebarItem | SidebarSection

export type CatalogEntry = {
  defaultLabel: string
  /** Where the item goes. May carry a query (`/ops?tab=sessions`) for an
   *  entry that opens one tab of a hub. */
  path: string
  icon: ReactNode
  /** For an item added after people saved their own layouts: the id it sits
   *  right after. mergeWithCatalog puts it there in a saved layout that has
   *  that item, instead of in the "New" section at the bottom. */
  after?: string
}

/** Stable item catalog. Each route the app exposes is registered here once.
 *
 *  PR 6 (information architecture): 22 entries, every label the page's own
 *  title (A7). The six PPC level entries (campaigns, ad-groups, targets,
 *  search-terms, placements, negatives) became ONE 'advertising' item - the
 *  hub's level strip is their navigation now - and Activity / Custom metrics /
 *  Saved filters are tabs on Automations, not items. Strategies, Listing Agent
 *  and Automation Drafts were folded into New campaign, the listing drawer and
 *  the New automation menu. A saved layout that still names a removed id is
 *  mapped by migrateLayout; every old route keeps working (AppRoutes). */
export const ITEM_CATALOG: Record<string, CatalogEntry> = {
  // WS1 (2026-06-15): Dashboard renamed to "Command Center" and moved to
  // /overview. /dashboard redirects to /overview in App.tsx so stale
  // bookmarks + persisted sidebar configs land somewhere.
  // P3 (Daily Brief, 2026-07-21): renamed again to "Daily Brief" — /overview
  // now defaults to the Daily Brief sub-tab (the in-app replacement for the
  // ClickUp-posted daily brief); the former Command Center content is still
  // there as the Dashboard sub-tab (?tab=dashboard), unchanged. Route/id
  // unchanged so nothing else (redirects, persisted configs) needs to move.
  'dashboard':    { defaultLabel: 'Daily Brief', path: '/overview',   icon: <LayoutDashboard size={16} /> },
  // SellerMate W3.3: business alerts (Buy Box lost while ads run, budget out
  // early, PO late…) — the log plus the saved alerts.
  'alerts':       { defaultLabel: 'Alerts',       path: '/alerts',       icon: <Siren size={16} /> },
  // PR 6: the Advertising hub (PR 4). Its level strip (Campaigns … Negatives)
  // replaces the six PPC items this one stands in for.
  'advertising':  { defaultLabel: 'Advertising',  path: ADS_PATH,        icon: <Megaphone size={14} /> },
  // SellerMate W1.8: plan new campaigns, preview them, stage them for approval.
  // PR 6: also hosts "From a strategy" (the former Strategies page).
  'campaign-builder': { defaultLabel: 'New campaign', path: '/campaigns/new', icon: <CirclePlus size={14} /> },
  // ClickPay Suggests (P4): read-only recommendation panel that STAGES one-click
  // moves into the approval queue (never auto-applies).
  'suggestions':  { defaultLabel: 'Suggestions', path: '/suggestions', icon: <Lightbulb size={14} /> },
  // PR 15 (plan §M1): deals and coupons on our products and how they did.
  'deals':        { defaultLabel: 'Deals',        path: '/deals',        icon: <BadgePercent size={14} /> },
  // SellerMate-upgrade W5.2: the Ads Overview report (/reports lands on it).
  'ads-overview': { defaultLabel: 'Ads overview', path: '/reports/ads-overview', icon: <PieChart size={14} /> },
  'hourly':       { defaultLabel: 'Hourly',       path: '/hourly',       icon: <Clock size={14} /> },
  // SellerMate W4.3: the weekly account audit.
  'account-audit': { defaultLabel: 'Account audit', path: '/audit', icon: <ClipboardCheck size={14} /> },
  // AMC Audiences (dark-launched behind CLICKPAY_AMC). The catalog entry must
  // exist so the route + label resolve, but visibleTree hides it unless
  // /api/amc/status.enabled is true — so with the flag off it never appears,
  // even after mergeWithCatalog folds new ids into a "New" section.
  'audiences':    { defaultLabel: 'Audiences',    path: '/audiences',    icon: <UsersRound size={14} /> },
  // 'asin' merged into 'products' (2026-05-25). Users with the old item in
  // their persisted sidebar config still resolve via the /asin → /products
  // redirect in App.tsx until they refresh the layout.
  // 'skus' merged into 'products' (2026-06-01) as inline expandable child rows.
  'products':     { defaultLabel: 'Products',     path: '/products',     icon: <Package size={14} /> },
  // Marketplaces hub (2026-06-19): the former Restock Hub became the unified
  // "Marketplaces" hub (Products/Orders/P&L/Listings/Listing-Health/Restock
  // sub-tabs). New canonical path /inventory/marketplaces; /inventory/restock-hub
  // still redirects (App.tsx) so stale bookmarks land here.
  'inv-restock':   { defaultLabel: 'Marketplaces',     path: '/inventory/marketplaces',    icon: <Store size={14} /> },
  // Orders / Shipments / Payments folded into ONE tab shell at /inventory/orders
  // (Timeline/Shipments/COGS); PR 6 titles it "Orders & shipments" (A7).
  'inv-orders':    { defaultLabel: 'Orders & shipments', path: '/inventory/orders',          icon: <Box size={14} /> },
  // Feedback #118/#119 (2026-09-23): every product's settings on one page —
  // hidden products included, which no hub tab can show.
  'inv-product-settings': { defaultLabel: 'Product settings', path: '/inventory/product-settings', icon: <SlidersHorizontal size={14} /> },
  // Automations port (CONTRACTS 10.2): /rules is the Automations hub; its
  // Activity / Custom metrics / Saved filters are tabs on the page (PR 6 took
  // their sidebar items out).
  'rules':        { defaultLabel: 'Automations',  path: '/rules',        icon: <ListChecks size={16} /> },
  // #36: the PPC change trail. It lives in Automations because that is where
  // the other "what did this system do, and why" surfaces are.
  'history':      { defaultLabel: 'Change history', path: '/history',     icon: <ScrollText size={16} /> },
  // Settings (product reviews #2 + #12; PR 7): ONE entry, labelled the same
  // as the page's H1 (A7). The former System sub-pages were folded into its
  // tabs and dropped from the catalog, so stale persisted sidebar configs
  // prune them via mergeWithCatalog on next load. The id stays 'ops' so saved
  // layouts keep it; prod's saved layout already labels it "Settings".
  'ops':          { defaultLabel: 'Settings',     path: '/ops',          icon: <SettingsIcon size={16} /> },
  // ── Labs (PR 6): admins only, folded by default. Pages kept for the people
  // who build ClickPay, out of the daily menu. ──
  'agents':       { defaultLabel: 'Agents',       path: '/agents',       icon: <Bot size={16} /> },
  // The workflow library (Phase 12 Build 37): the recurring jobs we do,
  // written down. This catalog is ALSO the source of the "screen this step
  // happens on" dropdown in the workflow editor (pages/Workflows.tsx), so a
  // screen renamed or retired here changes there too — one list, no drift.
  'workflows':    { defaultLabel: 'Workflows',    path: '/workflows',    icon: <BookOpen size={16} /> },
  // Settings' Sessions tab (admin-only there too).
  'sessions':     { defaultLabel: 'Sessions',     path: '/ops?tab=sessions', icon: <Radio size={16} /> },
  // Asking happens in the header's AI side panel; this page is the history.
  'ai-assistant': { defaultLabel: 'AI Assistant history', path: '/ai-assistant', icon: <MessageCircle size={16} /> },
  'rules-classic': { defaultLabel: 'Rules (classic)', path: '/rules/legacy', icon: <Wrench size={16} /> },
}

/** Generic icon used for user-created sections. */
export const SECTION_ICON = <BarChart3 size={14} />
export const CUSTOM_SECTION_ICON = <Folder size={14} />

const item = (id: string): SidebarItem => ({ type: 'item', id })

/** PR 6 (v3): one menu, every group named for what it holds. The ids `ads`
 *  and `catalog` are new on purpose - item ids `advertising` and `products`
 *  already exist and collapse state is keyed by section id. `rule-engine`
 *  keeps its id. */
export const DEFAULT_CONFIG: SidebarNode[] = [
  { type: 'item', id: 'dashboard' },
  { type: 'item', id: 'alerts' },
  { type: 'section', id: 'ads', label: 'Advertising', children: [
    'advertising', 'campaign-builder', 'suggestions', 'deals', 'ads-overview', 'hourly', 'account-audit', 'audiences',
  ].map(item) },
  { type: 'section', id: 'catalog', label: 'Products & Inventory', children: [
    'products', 'inv-restock', 'inv-orders', 'inv-product-settings',
  ].map(item) },
  { type: 'section', id: 'rule-engine', label: 'Automations', children: ['rules', 'history'].map(item) },
  { type: 'item', id: 'ops' },
  { type: 'section', id: 'labs', label: 'Labs', children: [
    'agents', 'workflows', 'sessions', 'ai-assistant', 'rules-classic',
  ].map(item) },
]

/** The admin-only group. Hidden from everyone else (visibleTree) and folded
 *  until someone opens it. */
export const LABS_SECTION_ID = 'labs'
/** Item ids the Ctrl+K page list hides from non-admins: the Labs pages that
 *  are admin tools. Workflows is NOT here - the library is read-only for
 *  everyone, so its Ctrl+K row stays open. */
export const LABS_ADMIN_ITEMS: ReadonlySet<string> = new Set(['agents', 'sessions', 'ai-assistant', 'rules-classic'])
export const DEFAULT_COLLAPSED: Record<string, boolean> = { [LABS_SECTION_ID]: true }

/** Saved-layout format. A bare array is v2 (and older); v3 is wrapped so the
 *  next change can tell what it is reading. */
export const LAYOUT_VERSION = 3
export type StoredLayout = { v: 3; nodes: SidebarNode[] }

const STORAGE_KEY = 'adbetter.sidebar.config.v3'
/** The v2 key is read once as a fallback (migrated and re-saved under v3). */
const STORAGE_KEY_V2 = 'adbetter.sidebar.config.v2'

/** Walk a tree collecting every item id. */
function collectItemIds(nodes: SidebarNode[], out: Set<string>): void {
  for (const n of nodes) {
    if (n.type === 'item') out.add(n.id)
    else collectItemIds(n.children, out)
  }
}

/** Drop unknown item ids (items removed from CATALOG). Also drop built-in
 *  sections that pruning leaves empty (e.g. the old System → Diagnostics
 *  subsection after its items folded into the Ops Hub), so a stale persisted
 *  config doesn't render an empty section header. User-made custom sections
 *  (custom_*) are kept even when empty — the operator may be mid-build. */
function pruneUnknown(nodes: SidebarNode[]): SidebarNode[] {
  return nodes
    .map(n => n.type === 'section' ? { ...n, children: pruneUnknown(n.children) } : n)
    .filter(n => {
      if (n.type === 'item') return ITEM_CATALOG[n.id] !== undefined
      return n.children.length > 0 || n.id.startsWith('custom_')
    })
}

/** The id of the DEFAULT_CONFIG section an item lives in (innermost), or null. */
function defaultSectionOf(id: string, nodes: SidebarNode[] = DEFAULT_CONFIG, parent: string | null = null): string | null {
  for (const n of nodes) {
    if (n.type === 'item') { if (n.id === id) return parent; continue }
    const found = defaultSectionOf(id, n.children, n.id)
    if (found) return found
  }
  return null
}

/** Append `item` to the section `sectionId` wherever it sits in `nodes`.
 *  Returns a new tree and whether the section was found. */
function appendToSection(nodes: SidebarNode[], sectionId: string, item: SidebarItem): [SidebarNode[], boolean] {
  let placed = false
  const next = nodes.map((n) => {
    if (placed || n.type !== 'section') return n
    if (n.id === sectionId) { placed = true; return { ...n, children: [...n.children, item] } }
    const [children, inner] = appendToSection(n.children, sectionId, item)
    if (inner) { placed = true; return { ...n, children } }
    return n
  })
  return [next, placed]
}

/** Insert `item` right after the item `afterId`, wherever it sits in the tree.
 *  Returns null when `afterId` is not in the tree. */
function insertAfterItem(nodes: SidebarNode[], afterId: string, item: SidebarItem): SidebarNode[] | null {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i]
    if (n.type === 'item' && n.id === afterId) {
      return [...nodes.slice(0, i + 1), item, ...nodes.slice(i + 1)]
    }
    if (n.type === 'section') {
      const children = insertAfterItem(n.children, afterId, item)
      if (children) return [...nodes.slice(0, i), { ...n, children }, ...nodes.slice(i + 1)]
    }
  }
  return null
}

/** Merge stored config with CATALOG so newly added routes always appear
 *  somewhere, tried in this order:
 *   1. right after their `after` item when the saved layout has it (so the
 *      Automations hub's tabs line up under Automations wherever someone moved
 *      it — Automations port, CONTRACTS 10.2);
 *   2. appended to their DEFAULT section when the stored layout still has it
 *      (so a new Inventory page lands under Inventory, not at the bottom — #118);
 *   3. otherwise in a trailing "New" section. */
export function mergeWithCatalog(stored: SidebarNode[]): SidebarNode[] {
  let merged = pruneUnknown(stored)
  const present = new Set<string>()
  collectItemIds(merged, present)
  const leftover: string[] = []
  // Catalog order, so a chain (rules-metrics after rules-activity after rules)
  // lands in order: each placed item is there for the next one to follow.
  for (const id of Object.keys(ITEM_CATALOG)) {
    if (present.has(id)) continue
    const item: SidebarItem = { type: 'item', id }
    const after = ITEM_CATALOG[id].after
    let placed = after ? insertAfterItem(merged, after, item) : null
    if (!placed) {
      const home = defaultSectionOf(id)
      const [next, found] = home ? appendToSection(merged, home, item) : [merged, false]
      if (found) placed = next
    }
    if (placed) {
      merged = placed
      present.add(id)
    } else {
      leftover.push(id)
    }
  }
  if (leftover.length === 0) return merged
  return [...merged, {
    type: 'section', id: `__new_${Date.now()}`, label: 'New',
    children: leftover.map(id => ({ type: 'item' as const, id })),
  }]
}

/**
 * Whether the sidebar entry at `path` matches the location. NavLink's own
 * test compares paths only, which cannot tell /ops from /ops?tab=sessions. An
 * entry whose path carries a query matches only on its exact path and only
 * when every one of its parameters matches; a plain entry matches its path and
 * anything below it at a '/' boundary (NavLink's rule). This is the ONE-entry
 * test: which entry the sidebar lights is activeCatalogId's call.
 */
export function isCatalogPathActive(path: string, pathname: string, search: string): boolean {
  const [entryPath, entryQuery = ''] = path.split('?')
  const under = pathname === entryPath || pathname.startsWith(`${entryPath}/`)
  if (!under) return false
  const current = new URLSearchParams(search)
  if (entryQuery) {
    if (pathname !== entryPath) return false
    // The hub reads a missing/aliased tab as a level (bare /ads = Campaigns,
    // ?tab=keywords = Targets); compare what it shows, not the raw query.
    const cur = pathname === ADS_PATH ? readAdsLocation(search) : null
    for (const [k, v] of new URLSearchParams(entryQuery)) {
      const have = cur && k === 'tab' ? cur.tab : cur && k === 'kind' ? cur.kind : current.get(k)
      if (have !== v) return false
    }
  }
  return true
}

/** The ONE catalog id the sidebar lights for a location, or null (A9).
 *  Candidates are the entries isCatalogPathActive accepts; the longest entry
 *  path wins, so /rules/legacy lights Rules (classic) and not Automations,
 *  and among equals the query-carrying entry wins (/ops?tab=sessions over
 *  /ops). */
export function activeCatalogId(
  pathname: string, search: string, candidates?: ReadonlySet<string>,
): string | null {
  let best: string | null = null
  let bestLen = -1
  let bestQuery = false
  for (const [id, entry] of Object.entries(ITEM_CATALOG)) {
    // Only items this viewer can actually see may win: a hidden Labs item
    // must not steal the highlight from the visible one (Automations).
    if (candidates && !candidates.has(id)) continue
    if (!isCatalogPathActive(entry.path, pathname, search)) continue
    const [entryPath, entryQuery = ''] = entry.path.split('?')
    const hasQuery = entryQuery !== ''
    if (entryPath.length > bestLen || (entryPath.length === bestLen && hasQuery && !bestQuery)) {
      best = id
      bestLen = entryPath.length
      bestQuery = hasQuery
    }
  }
  return best
}

export function isCatalogItemActive(
  id: string, pathname: string, search: string, candidates?: ReadonlySet<string>,
): boolean {
  return activeCatalogId(pathname, search, candidates) === id
}

// ── Layout migration (v2 → v3) ──────────────────────────────────────────────

/** Saved ids that no longer exist, and the item that replaced them. Every
 *  other id maps to itself when it is still in the catalog and is dropped when
 *  it is not (strategies, listing, automation-drafts, rules-activity, ...). */
const ID_MAP: Record<string, string> = {
  'campaigns': 'advertising',
  'ad-groups': 'advertising',
  'targets': 'advertising',
  'search-terms': 'advertising',
  'placements': 'advertising',
  'negatives': 'advertising',
}

/** Rebuild a person's own group for v3: ids mapped, repeats and unknown ids
 *  dropped, hand-set item labels dropped (labels are the page titles now, A7).
 *  `seen` collects every item id carried so the caller can take them out of
 *  the default tree. */
function rebuildCustom(section: SidebarSection, seen: Set<string>): SidebarSection {
  const children: SidebarNode[] = []
  for (const c of section.children) {
    if (c.type === 'section') {
      if (c.id.startsWith('custom_')) children.push(rebuildCustom(c, seen))
      continue
    }
    const id = ID_MAP[c.id] ?? c.id
    if (ITEM_CATALOG[id] === undefined || seen.has(id)) continue
    seen.add(id)
    children.push({ type: 'item', id })
  }
  return { type: 'section', id: section.id, label: section.label, children }
}

/** Every custom_* group in a v2 tree, in stored order (a custom group inside a
 *  built-in one is lifted out: the built-in groups are not carried). */
function collectCustom(nodes: unknown, out: SidebarSection[]): void {
  if (!Array.isArray(nodes)) return
  for (const n of nodes as SidebarNode[]) {
    if (!n || n.type !== 'section') continue
    if (typeof n.id === 'string' && n.id.startsWith('custom_')) out.push(n)
    else collectCustom(n.children, out)
  }
}

function withoutItems(nodes: SidebarNode[], drop: Set<string>): SidebarNode[] {
  const out: SidebarNode[] = []
  for (const n of nodes) {
    if (n.type === 'item') { if (!drop.has(n.id)) out.push(n) }
    else out.push({ ...n, children: withoutItems(n.children, drop) })
  }
  return out
}

/**
 * Whatever the server or the browser kept → the v3 tree. Pure, total and
 * idempotent.
 *  - `{v:3, nodes}` passes through mergeWithCatalog only (migrated: false).
 *  - A bare array is v2: the standard v3 layout, plus the person's OWN groups
 *    (custom_*) with their items mapped, de-duplicated and unlabelled, placed
 *    after Settings and before Labs. Built-in groups, their order and any
 *    hand-set labels are not carried: the owner approved the new grouping.
 *  - Anything else (null, a string, an unknown version) is the standard layout.
 * `migrated` says the result differs from what was stored, so an admin's
 * browser knows to write the v3 wrapper back to the server once.
 */
export function migrateLayout(stored: unknown): { nodes: SidebarNode[]; migrated: boolean } {
  if (stored && typeof stored === 'object' && !Array.isArray(stored)
      && (stored as { v?: unknown }).v === LAYOUT_VERSION
      && Array.isArray((stored as { nodes?: unknown }).nodes)) {
    return { nodes: mergeWithCatalog((stored as StoredLayout).nodes), migrated: false }
  }
  if (!Array.isArray(stored)) return { nodes: DEFAULT_CONFIG, migrated: true }

  const customs: SidebarSection[] = []
  collectCustom(stored, customs)
  const seen = new Set<string>()
  const rebuilt = customs.map(s => rebuildCustom(s, seen))
  let tree = withoutItems(DEFAULT_CONFIG, seen)
  if (rebuilt.length > 0) {
    const labsAt = tree.findIndex(n => n.type === 'section' && n.id === LABS_SECTION_ID)
    const at = labsAt < 0 ? tree.length : labsAt
    tree = [...tree.slice(0, at), ...rebuilt, ...tree.slice(at)]
  }
  return { nodes: mergeWithCatalog(tree), migrated: true }
}

function readStored(key: string): unknown {
  const raw = window.localStorage.getItem(key)
  return raw ? JSON.parse(raw) : null
}

export function loadConfig(): SidebarNode[] {
  if (typeof window === 'undefined') return DEFAULT_CONFIG
  try {
    const v3 = readStored(STORAGE_KEY)
    if (v3) return migrateLayout(v3).nodes
    // PR 6: the v2 copy is read once, migrated and kept under the v3 key.
    const v2 = readStored(STORAGE_KEY_V2)
    if (v2) {
      const { nodes } = migrateLayout(v2)
      saveConfig(nodes)
      return nodes
    }
    return DEFAULT_CONFIG
  } catch {
    return DEFAULT_CONFIG
  }
}

export function saveConfig(config: SidebarNode[]): void {
  const stored: StoredLayout = { v: LAYOUT_VERSION, nodes: config }
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored)) } catch { /* ignore */ }
}

export function resetConfig(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(STORAGE_KEY_V2)
  } catch { /* ignore */ }
}

// ── What a person sees ──────────────────────────────────────────────────────

/** A copy of the tree without `items` and `sections`; a built-in group the
 *  removal leaves empty goes too (mirrors pruneUnknown), so a hidden group
 *  never leaves a bare header. Custom groups (custom_*) are kept even when
 *  empty - the operator may be mid-build. */
function filterTree(nodes: SidebarNode[], items: Set<string>, sections: Set<string>): SidebarNode[] {
  const out: SidebarNode[] = []
  for (const n of nodes) {
    if (n.type === 'item') {
      if (!items.has(n.id)) out.push(n)
    } else if (!sections.has(n.id)) {
      const children = filterTree(n.children, items, sections)
      if (children.length > 0 || n.id.startsWith('custom_')) out.push({ ...n, children })
    }
  }
  return out
}

/** Depth-first list of every item a tree renders, sections dropped. The rail
 *  has no room for group headers, but every catalog entry carries an icon, so
 *  collapsing costs the labels and not the navigation. */
export function flattenItems(nodes: SidebarNode[]): SidebarItem[] {
  const out: SidebarItem[] = []
  for (const n of nodes) {
    if (n.type === 'item') out.push(n)
    else out.push(...flattenItems(n.children))
  }
  return out
}

/** Ids of the items a read-mode menu actually shows: items inside a folded
 *  group are not shown, so they cannot hold the highlight either. */
export function renderedItemIds(nodes: SidebarNode[], collapsed: Record<string, boolean>): Set<string> {
  const out = new Set<string>()
  const walk = (ns: SidebarNode[]) => {
    for (const n of ns) {
      if (n.type === 'item') out.add(n.id)
      else if (collapsed[n.id] !== true) walk(n.children)
    }
  }
  walk(nodes)
  return out
}

/** What a person sees: Labs only for admins; Audiences only with AMC on.
 *  Render-only - the saved layout is never touched (the gate must live at
 *  render time: mergeWithCatalog is pure and would otherwise surface a gated
 *  id in a "New" group). Edit mode renders the full config instead. */
export function visibleTree(
  nodes: SidebarNode[],
  who: { isAdmin: boolean; amcEnabled: boolean },
): SidebarNode[] {
  const items = new Set<string>()
  const sections = new Set<string>()
  if (!who.amcEnabled) items.add('audiences')
  if (!who.isAdmin) {
    sections.add(LABS_SECTION_ID)
    // Gate by item too, so an admin moving a Labs page out of the Labs group
    // (or deleting the group) cannot expose it to everyone else.
    for (const id of LABS_ADMIN_ITEMS) items.add(id)
  }
  if (items.size === 0 && sections.size === 0) return nodes
  return filterTree(nodes, items, sections)
}

// ── Pin state (W0.5) ────────────────────────────────────────────────────────
// The desktop sidebar is either PINNED open (full width, pushes the page
// over) or UNPINNED: an icon rail that slides open over the page while the
// pointer is on it. The choice is personal, so it lives next to the layout in
// this store but on its own key and is never pushed to the server — the
// server layout is the one global menu an admin edits for everyone, and one
// person folding their sidebar must not fold it for the team. "Reset" on the
// layout also leaves it alone: resetting the menu is not collapsing it.
//
// Before W0.5 the same choice was the rail toggle under
// `clickpay.sidebar.rail.v1` ('1' = folded to icons). That key is read once
// as the fallback, so nobody who had folded their sidebar finds it open.

/** The two Storage methods the pin store needs — lets tests pass a fake. */
export type PinStore = Pick<Storage, 'getItem' | 'setItem'>

export const SIDEBAR_PIN_KEY = 'adbetter.sidebar.pinned.v1'
export const LEGACY_RAIL_KEY = 'clickpay.sidebar.rail.v1'

/** Widths published as `--sidebar-w` (RestockHub drawers size off it). */
export const SIDEBAR_RAIL_W = '3.25rem'   // must track `w-[3.25rem]` in Sidebar.tsx
export const SIDEBAR_FULL_W = '15rem'     // must track `w-60` in Sidebar.tsx

function browserStore(): PinStore | null {
  try { return window.localStorage } catch { return null }
}

/** True = pinned open. Defaults to pinned (the layout everyone had before). */
export function loadPinned(store: PinStore | null = browserStore()): boolean {
  if (!store) return true
  try {
    const v = store.getItem(SIDEBAR_PIN_KEY)
    if (v === '1') return true
    if (v === '0') return false
    return store.getItem(LEGACY_RAIL_KEY) !== '1'
  } catch {
    return true
  }
}

export function savePinned(pinned: boolean, store: PinStore | null = browserStore()): void {
  if (!store) return
  try { store.setItem(SIDEBAR_PIN_KEY, pinned ? '1' : '0') } catch { /* ignore */ }
}

/** The phone drawer's id - the header hamburger's aria-controls. */
export const SIDEBAR_DRAWER_ID = 'app-nav-drawer'

/** Does this pointer open the unpinned rail on hover? Only a mouse: a tap
 *  on a touch screen fires pointerenter just before its click, which would
 *  swap the rail for the full panel under the finger (the tap then lands on
 *  a different item) with nothing to fold it back. Touch and pen users open
 *  the menu with the Pin button, or tap a rail icon directly. */
export function railOpensOnHover(pointerType: string): boolean {
  return pointerType === 'mouse'
}

/** The width the page must leave for the sidebar. An unpinned rail opens
 *  OVER the page, so it only ever reserves the rail; below the desktop
 *  breakpoint the sidebar is an off-canvas drawer and reserves nothing. */
export function reservedSidebarWidth(pinned: boolean, desktop: boolean): string {
  if (!desktop) return '0rem'
  return pinned ? SIDEBAR_FULL_W : SIDEBAR_RAIL_W
}

// ── server-side persistence (#7) ────────────────────────────────────────────
// PR 6 splits what is saved in two. The MENU (order, groups, labels) is one
// global record in cp_global_sidebar_layouts that only an admin may write
// (TRACK C). Which groups a person has FOLDED is their own preference
// (cp_sidebar_user_prefs, migration 285): before, the admin's fold state rode
// along in the global row and overwrote everyone's on every load (D8).
// localStorage stays as the instant first-paint cache and the dev-mode
// (CLICKPAY_AUTH=off) store. Every call is same-origin + cookie auth; a 401 /
// empty / any error degrades silently to the localStorage path.

export type SidebarApp = 'main' | 'ops'

/** The saved menu exactly as stored (a v3 wrapper, or a bare v2 array):
 *  the caller runs it through migrateLayout. */
export type ServerLayout = { layout: unknown }

/** The one raw network call of this module: same-origin, cookie auth. Resolves
 *  to the parsed JSON body, or null for any non-OK answer (401 logged out / dev
 *  mode) or error - every caller degrades to the localStorage copy. */
async function sidebarRequest(url: string, init?: { method: 'PUT'; body: unknown }): Promise<unknown | null> {
  try {
    const res = await fetch(url, {
      credentials: 'same-origin',
      method: init?.method,
      headers: init ? { 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: init ? JSON.stringify(init.body) : undefined,
    })
    if (!res.ok) return null
    return init ? true : await res.json()
  } catch {
    return null              // offline / logged out
  }
}

export async function fetchServerConfig(app: SidebarApp = 'main'): Promise<ServerLayout | null> {
  const body = await sidebarRequest(`/api/sidebar/layout?app=${app}`) as { layout?: unknown } | null
  if (!body || !body.layout) return null // {} = no saved layout for this user
  return { layout: body.layout }
}

/** Admin only (the server refuses anyone else). `collapsed` is always null:
 *  fold state is no longer part of the global record. */
export async function pushServerConfig(app: SidebarApp, nodes: SidebarNode[]): Promise<void> {
  const layout: StoredLayout = { v: LAYOUT_VERSION, nodes }
  await sidebarRequest('/api/sidebar/layout', { method: 'PUT', body: { app, layout, collapsed: null } })
}

/** This person's own fold state, or null when none is saved / not signed in. */
export async function fetchServerPrefs(app: SidebarApp = 'main'): Promise<Record<string, boolean> | null> {
  const body = await sidebarRequest(`/api/sidebar/prefs?app=${app}`) as { collapsed?: unknown } | null
  const c = body?.collapsed
  if (!c || typeof c !== 'object' || Array.isArray(c)) return null
  return c as Record<string, boolean>
}

/** Save this person's fold state - any signed-in user, their own row. */
export async function pushServerPrefs(app: SidebarApp, collapsed: Record<string, boolean>): Promise<void> {
  await sidebarRequest('/api/sidebar/prefs', { method: 'PUT', body: { app, collapsed } })
}

/** Resolve a node's display label: user override first, then catalog default. */
export function nodeLabel(n: SidebarNode): string {
  if (n.type === 'section') return n.label
  return n.label ?? ITEM_CATALOG[n.id]?.defaultLabel ?? n.id
}

/** Walk a tree applying a transform; returns a new tree. */
export function mapTree(nodes: SidebarNode[], fn: (n: SidebarNode) => SidebarNode): SidebarNode[] {
  return nodes.map(n => {
    const next = fn(n)
    if (next.type === 'section') return { ...next, children: mapTree(next.children, fn) }
    return next
  })
}

/** Path = array of indices from root to node. Used for drag-drop + edits. */
export type NodePath = number[]

export function getNode(nodes: SidebarNode[], path: NodePath): SidebarNode | undefined {
  let cur: SidebarNode | undefined
  let arr: SidebarNode[] | undefined = nodes
  for (const i of path) {
    if (!arr || i < 0 || i >= arr.length) return undefined
    cur = arr[i]
    arr = cur.type === 'section' ? cur.children : undefined
  }
  return cur
}

/** Remove the node at path, return [newTree, removedNode]. */
export function removeAt(nodes: SidebarNode[], path: NodePath): [SidebarNode[], SidebarNode | undefined] {
  if (path.length === 0) return [nodes, undefined]
  const [i, ...rest] = path
  if (rest.length === 0) {
    const removed = nodes[i]
    return [[...nodes.slice(0, i), ...nodes.slice(i + 1)], removed]
  }
  const parent = nodes[i]
  if (parent.type !== 'section') return [nodes, undefined]
  const [newChildren, removed] = removeAt(parent.children, rest)
  return [[
    ...nodes.slice(0, i),
    { ...parent, children: newChildren },
    ...nodes.slice(i + 1),
  ], removed]
}

/** Insert node at path (path points to the index it should occupy). */
export function insertAt(nodes: SidebarNode[], path: NodePath, node: SidebarNode): SidebarNode[] {
  if (path.length === 0) return nodes
  const [i, ...rest] = path
  if (rest.length === 0) {
    return [...nodes.slice(0, i), node, ...nodes.slice(i)]
  }
  const parent = nodes[i]
  if (parent.type !== 'section') return nodes
  return [
    ...nodes.slice(0, i),
    { ...parent, children: insertAt(parent.children, rest, node) },
    ...nodes.slice(i + 1),
  ]
}

/** Check if `descendant` is at or under `ancestor` path. Prevents dropping a section into itself. */
export function isDescendant(ancestor: NodePath, descendant: NodePath): boolean {
  if (descendant.length < ancestor.length) return false
  for (let i = 0; i < ancestor.length; i++) {
    if (ancestor[i] !== descendant[i]) return false
  }
  return true
}
